import React, { useEffect, useState } from 'react';
import { 
  Button, Heading, Stack, Text, Box, Spinner, Alert, AlertIcon, 
  Table, Tbody, Tr, Td, Badge, Divider 
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

// Icono taxi para el destino
const taxiIcon = new L.Icon({
  iconUrl: '/assets/images/taxi-marker.png',
  iconSize: [32, 48],
  iconAnchor: [16, 48],
  popupAnchor: [0, -48],
  shadowUrl: null,
  shadowSize: null,
  shadowAnchor: null,
});

// Fix para el icono de Leaflet por defecto (recogida)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

// Componente auxiliar para ajustar el mapa a los puntos
function FitBounds({ puntos }) {
  const map = useMap();
  React.useEffect(() => {
    if (puntos.length === 0) return;
    if (puntos.length === 1) {
      map.setView(puntos[0], 15);
    } else {
      const bounds = L.latLngBounds(puntos);
      map.fitBounds(bounds, { padding: [40, 40] });
    }
  }, [puntos, map]);
  return null;
}

const FaseConfirmacion = ({ datosReserva, onRetry, onBack }) => {
  const { t, i18n } = useTranslation();
  const { user, setPromocode } = useAuth();
  const [reservaCompleta, setReservaCompleta] = useState(null);
  const [clienteInfo, setClienteInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagoConfirmado, setPagoConfirmado] = useState(false);

  // Detectar entorno: localhost = testing, otherwise = producción
  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const homeUrl = isLocalhost ? 'http://localhost:3000/' : 'https://www.overtaxi.es/';
  const apiUrl = isLocalhost ? 'http://localhost:8000' : 'https://www.overtaxi.es';

  useEffect(() => {
    const fetchReservaData = async () => {
      if (!datosReserva.reserva_id && !datosReserva.reservaId) {
        setError('No se encontró el ID de la reserva');
        setLoading(false);
        return;
      }

      const reservaId = datosReserva.reserva_id || datosReserva.reservaId;

      try {
        setLoading(true);
        
        const token = localStorage.getItem('auth-token');
        console.log('[FaseConfirmacion] Token encontrado:', token ? 'Sí' : 'No');
        console.log('[FaseConfirmacion] datosReserva.estado:', datosReserva.estado);
        
        // Si llegamos con estado=ok desde Redsys, primero confirmar el pago antes de mostrar
        if (token && datosReserva.estado === 'ok' && !pagoConfirmado) {
          try {
            console.log('[FaseConfirmacion] Confirmando pago con estado=ok antes de cargar datos...');
            console.log('[FaseConfirmacion] Llamando a:', `${apiUrl}/api/pagos/confirmar-pago/${reservaId}`);
            console.log('[FaseConfirmacion] Idioma actual:', i18n.language);
            
            // Capturar parámetros de Redsys de la URL (si existen)
            const urlParams = new URLSearchParams(window.location.search);
            const merchantParams = urlParams.get('Ds_MerchantParameters');
            const signature = urlParams.get('Ds_Signature');
            const signatureVersion = urlParams.get('Ds_SignatureVersion');
            
            console.log('[FaseConfirmacion] Parámetros Redsys capturados:', {
              merchantParams: merchantParams ? 'Sí' : 'No',
              signature: signature ? 'Sí' : 'No',
              signatureVersion: signatureVersion
            });
            
            setPagoConfirmado(true); // Marcar como procesado ANTES de la llamada
            const confirmacionResponse = await axios.post(
              `${apiUrl}/api/pagos/confirmar-pago/${reservaId}`,
              { 
                idioma: i18n.language,
                Ds_MerchantParameters: merchantParams,
                Ds_Signature: signature,
                Ds_SignatureVersion: signatureVersion
              },
              { headers: { Authorization: `Bearer ${token}` } }
            );
            console.log('[FaseConfirmacion] Respuesta de confirmación:', confirmacionResponse.data);
            console.log('[FaseConfirmacion] Pago confirmado y email enviado');
          } catch (emailError) {
            console.error('[FaseConfirmacion] Error confirmando pago:', emailError);
            console.error('[FaseConfirmacion] Detalles del error:', emailError.response?.data);
            setPagoConfirmado(false); // Resetear si falló
            // No mostramos error al usuario, es un proceso en segundo plano
          }
        }
        
        // Ahora obtener datos de la reserva (ya con el estado actualizado si se confirmó el pago)
        const reservaResponse = await axios.get(`${apiUrl}/api/reservas/${reservaId}/`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        const reserva = reservaResponse.data;
        console.log('[FaseConfirmacion] Estado de reserva obtenido:', reserva.estado, '-', reserva.sub_estado);
        setReservaCompleta(reserva);

        // Obtener datos del cliente
        if (token) {
          const clienteResponse = await axios.get(`${apiUrl}/api/clientes/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setClienteInfo(clienteResponse.data);
        }

        // Limpiar promocode del contexto cuando la reserva se confirma exitosamente
        const estadoExitoso = datosReserva.estado === 'ok' || reserva.estado === 'activo';
        if (estadoExitoso && user?.promocode) {
          console.log('[FaseConfirmacion] Reserva confirmada exitosamente, limpiando promocode del contexto');
          setPromocode(null);
        }

        setLoading(false);
      } catch (err) {
        console.error('[FaseConfirmacion] Error obteniendo datos:', err);
        setError('Error al cargar los detalles de la reserva');
        setLoading(false);
      }
    };

    fetchReservaData();
  }, [datosReserva, apiUrl, i18n.language, pagoConfirmado, user?.promocode, setPromocode]);

  const handleGoHome = () => {
    window.location.href = homeUrl;
  };

  if (loading) {
    return (
      <Stack spacing={6} align="center" py={10}>
        <Spinner size="xl" color="purple.500" thickness="4px" />
        <Text>{t('confirmacion_cargando')}</Text>
      </Stack>
    );
  }

  if (error || !reservaCompleta) {
    return (
      <Stack spacing={6} align="center" py={10}>
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          {error || t('confirmacion_error_datos')}
        </Alert>
        <Button colorScheme="teal" onClick={onRetry}>
          {t('reintentar')}
        </Button>
        <Button variant="outline" onClick={handleGoHome}>
          {t('volver_inicio')}
        </Button>
      </Stack>
    );
  }

  const estadoExitoso = datosReserva.estado === 'ok' || reservaCompleta.estado === 'activo';
  // Las coordenadas vienen como "lng,lat" desde PostGIS, pero Leaflet necesita [lat, lng]
  const coordsOrigen = reservaCompleta.origen 
    ? reservaCompleta.origen.split(',').reverse() 
    : ['N/A', 'N/A'];
  const coordsDestino = reservaCompleta.destino 
    ? reservaCompleta.destino.split(',').reverse() 
    : ['N/A', 'N/A'];

  return (
    <Box maxW="700px" mx="auto" py={8}>
      <Stack spacing={6}>
        
        {/* Header con gradiente */}
        <Box
          bgGradient={estadoExitoso ? "linear(to-r, purple.500, purple.700)" : "linear(to-r, orange.500, orange.700)"}
          color="white"
          p={8}
          borderRadius="lg"
          textAlign="center"
          boxShadow="lg"
        >
          <Heading size="lg" mb={2}>
            {estadoExitoso ? t('confirmacion_titulo_exito') : t('confirmacion_titulo_pendiente')}
          </Heading>
        </Box>

        {/* Información del Cliente */}
        {clienteInfo && (
          <Box bg="gray.50" p={6} borderRadius="lg" boxShadow="sm">
            <Heading size="md" color="purple.600" mb={4} borderBottom="2px solid" borderColor="purple.600" pb={2}>
              {t('confirmacion_cliente_titulo')}
            </Heading>
            <Table variant="unstyled" size="sm">
              <Tbody>
                <Tr>
                  <Td fontWeight="600" w="40%" color="gray.700">{t('confirmacion_cliente_nombre')}:</Td>
                  <Td color="gray.900">{clienteInfo.nombre} {clienteInfo.apellidos}</Td>
                </Tr>
                {!user?.isAnonymous && (
                  <>
                    <Tr>
                      <Td fontWeight="600" color="gray.700">{t('confirmacion_cliente_email')}:</Td>
                      <Td color="purple.600">{clienteInfo.email}</Td>
                    </Tr>
                    <Tr>
                      <Td fontWeight="600" color="gray.700">{t('confirmacion_cliente_telefono')}:</Td>
                      <Td color="purple.600">{clienteInfo.telefono}</Td>
                    </Tr>
                  </>
                )}
              </Tbody>
            </Table>
          </Box>
        )}

        {/* Detalles de la Reserva */}
        <Box bg="green.50" p={6} borderRadius="lg" boxShadow="sm">
          <Heading size="md" color="green.600" mb={4} borderBottom="2px solid" borderColor="green.600" pb={2}>
            {t('confirmacion_detalles_titulo')}
          </Heading>
          <Table variant="unstyled" size="sm">
            <Tbody>
              <Tr>
                <Td fontWeight="600" w="40%" color="green.800">{t('confirmacion_id_reserva')}:</Td>
                <Td><Badge colorScheme="green" fontSize="md">#{reservaCompleta.id}</Badge></Td>
              </Tr>
              <Tr>
                <Td fontWeight="600" color="green.800">{t('confirmacion_tipo_servicio')}:</Td>
                <Td color="green.900" textTransform="uppercase">{reservaCompleta.tipo}</Td>
              </Tr>
              <Tr>
                <Td fontWeight="600" color="green.800">{t('confirmacion_origen')}:</Td>
                <Td color="green.900">
                  {reservaCompleta.origen_direccion || (coordsOrigen[0] !== 'N/A' ? `${coordsOrigen[0]}, ${coordsOrigen[1]}` : t('confirmacion_ver_mapa'))}
                </Td>
              </Tr>
              <Tr>
                <Td fontWeight="600" color="green.800">{t('confirmacion_destino')}:</Td>
                <Td color="green.900">
                  {reservaCompleta.destino_direccion || (coordsDestino[0] !== 'N/A' ? `${coordsDestino[0]}, ${coordsDestino[1]}` : t('confirmacion_ver_mapa'))}
                </Td>
              </Tr>
              <Tr>
                <Td fontWeight="600" color="green.800">{t('confirmacion_distancia')}:</Td>
                <Td color="green.900">{reservaCompleta.distancia_km} km</Td>
              </Tr>
              <Tr>
                <Td fontWeight="600" color="green.800">{t('confirmacion_precio')}:</Td>
                <Td><Text fontSize="xl" fontWeight="bold" color="green.600">{reservaCompleta.precio}€</Text></Td>
              </Tr>
              <Tr>
                <Td fontWeight="600" color="green.800">{t('confirmacion_precio_reserva')}:</Td>
                <Td><Text fontSize="xl" fontWeight="bold" color="blue.600">{reservaCompleta.precio_reserva || '3'}€</Text></Td>
              </Tr>
              <Tr>
                <Td fontWeight="600" color="green.800">{t('confirmacion_fecha_hora')}:</Td>
                <Td color="green.900">{new Date(reservaCompleta.fecha_hora_inicio).toLocaleString('es-ES')}</Td>
              </Tr>
              <Tr>
                <Td fontWeight="600" color="green.800">{t('confirmacion_estado')}:</Td>
                <Td>
                  <Badge colorScheme={estadoExitoso ? 'green' : 'yellow'} fontSize="sm" px={3} py={1} borderRadius="full">
                    {reservaCompleta.estado?.toUpperCase() === 'ACTIVO' && reservaCompleta.sub_estado?.toUpperCase() === 'BOLSA' 
                      ? t('confirmacion_estado_pendiente_conductor')
                      : `${reservaCompleta.estado?.toUpperCase()} - ${reservaCompleta.sub_estado?.toUpperCase()}`
                    }
                  </Badge>
                </Td>
              </Tr>
              <Tr>
                <Td fontWeight="600" color="green.800">{t('confirmacion_metodo_pago')}:</Td>
                <Td>
                  <Badge colorScheme="green" fontSize="sm" px={3} py={1} borderRadius="full">
                    {t('confirmacion_metodo_pago_tarjeta')}
                  </Badge>
                </Td>
              </Tr>
            </Tbody>
          </Table>
        </Box>

        {/* Mapa con puntos de origen y destino */}
        <Box w="100%" h="350px" borderRadius="lg" overflow="hidden" boxShadow="md">
          <MapContainer style={{ height: "100%", width: "100%" }} zoom={13} scrollWheelZoom={false}>
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            />
            {/* Punto de origen */}
            {coordsOrigen[0] !== 'N/A' && (
              <Marker position={[parseFloat(coordsOrigen[0]), parseFloat(coordsOrigen[1])]}>
                <Popup>{t('confirmacion_origen_mapa')}{reservaCompleta.origen_codigo_postal && ` - CP: ${reservaCompleta.origen_codigo_postal}`}</Popup>
              </Marker>
            )}
            {/* Punto de destino */}
            {coordsDestino[0] !== 'N/A' && (
              <Marker position={[parseFloat(coordsDestino[0]), parseFloat(coordsDestino[1])]} icon={taxiIcon}>
                <Popup>{t('confirmacion_destino_mapa')}{reservaCompleta.destino_codigo_postal && ` - CP: ${reservaCompleta.destino_codigo_postal}`}</Popup>
              </Marker>
            )}
            {/* Ajustar la vista para mostrar ambos puntos */}
            {coordsOrigen[0] !== 'N/A' && coordsDestino[0] !== 'N/A' && (
              <FitBounds puntos={[
                [parseFloat(coordsOrigen[0]), parseFloat(coordsOrigen[1])],
                [parseFloat(coordsDestino[0]), parseFloat(coordsDestino[1])]
              ]} />
            )}
          </MapContainer>
        </Box>

        {/* Alerta de estado */}
        {estadoExitoso ? (
          <Alert status="success" borderRadius="md" borderLeft="4px solid" borderColor="green.500" bg="green.50">
            <AlertIcon />
            <Box>
              <Text fontWeight="600" color="green.800">{t('confirmacion_pago_completado_titulo')}</Text>
              <Text fontSize="sm" color="green.700">{t('confirmacion_pago_completado_desc')}</Text>
            </Box>
          </Alert>
        ) : (
          <Alert status="warning" borderRadius="md" borderLeft="4px solid" borderColor="orange.500" bg="orange.50">
            <AlertIcon />
            <Box>
              <Text fontWeight="600" color="orange.800">{t('confirmacion_pendiente_titulo')}</Text>
              <Text fontSize="sm" color="orange.700">{t('confirmacion_pendiente_desc')}</Text>
            </Box>
          </Alert>
        )}

        <Divider />

        {/* Mensaje de contacto y cancelación */}
        <Box 
          bgGradient="linear(to-r, purple.500, purple.700)" 
          color="white" 
          p={6} 
          borderRadius="lg" 
          boxShadow="md"
          textAlign="center"
        >
          <Text fontSize="md" lineHeight="tall">
            {t('confirmacion_mensaje_contacto')}
          </Text>
        </Box>

        {/* Botones de acción */}
        {estadoExitoso ? (
          <Button 
            colorScheme="purple" 
            size="lg" 
            onClick={handleGoHome}
            width="full"
            boxShadow="md"
            _hover={{ transform: 'translateY(-2px)', boxShadow: 'lg' }}
            transition="all 0.2s"
          >
            {t('confirmacion_boton_inicio')}
          </Button>
        ) : (
          <Stack spacing={3} width="full">
            <Button 
              colorScheme="orange" 
              size="lg" 
              onClick={() => onBack('pago')}
              width="full"
              boxShadow="md"
              _hover={{ transform: 'translateY(-2px)', boxShadow: 'lg' }}
              transition="all 0.2s"
            >
              {t('confirmacion_boton_reintentar_pago')}
            </Button>
            <Button 
              colorScheme="purple" 
              variant="outline"
              size="lg" 
              onClick={handleGoHome}
              width="full"
              _hover={{ transform: 'translateY(-2px)' }}
              transition="all 0.2s"
            >
              {t('confirmacion_boton_inicio')}
            </Button>
          </Stack>
        )}
      </Stack>
    </Box>
  );
};

export default FaseConfirmacion;